import { useQuery } from '@tanstack/react-query'
import { repository } from '../../data'
import { Glyph } from './glyphs'
import type { GlyphName } from './glyphs'

/**
 * Four numbers across the top of Inventory, the way every stock tool opens:
 * what we hold, what is on the shelf, what came in this week, and what is
 * waiting for a label. Counted on the server by `summariseInventory`.
 */
export function SummaryStrip(): JSX.Element | null {
  const summary = useQuery({ queryKey: ['inventory', 'summary'], queryFn: () => repository.inventorySummary() })
  const s = summary.data
  if (!s) return null
  const attention = [s.out > 0 ? `${s.out} out` : null, s.low > 0 ? `${s.low} low` : null].filter(Boolean)
  return (
    <div className="inv-kpis">
      <Kpi icon="box" label="Items" value={s.items} sub={attention.join(' · ') || 'all in stock'} tone={s.out + s.low > 0 ? 'warning' : null} />
      <Kpi
        icon="shelf"
        label="Units on the shelf"
        value={s.unitsOnShelf}
        sub={s.bulkInStock > 0 ? `+ ${s.bulkInStock} bulk item${s.bulkInStock === 1 ? '' : 's'}` : 'serialized parts'}
      />
      <Kpi
        icon="truck"
        label="Deliveries, 7 days"
        value={s.deliveriesThisWeek}
        sub={s.deliveriesThisWeek === 0 ? 'nothing yet' : `of ${s.itemsThisWeek} item${s.itemsThisWeek === 1 ? '' : 's'}`}
      />
      <Kpi
        icon="tag"
        label="Labels to print"
        value={s.labelBacklog}
        sub={s.labelBacklog === 0 ? 'none waiting' : 'waiting for a printer'}
        tone={s.labelBacklog > 0 ? 'info' : null}
      />
    </div>
  )
}

function Kpi({
  icon,
  label,
  value,
  sub,
  tone = null,
}: {
  icon: GlyphName
  label: string
  value: number
  sub: string
  tone?: 'warning' | 'info' | null
}): JSX.Element {
  return (
    <div className={`inv-kpi${tone ? ` inv-kpi--${tone}` : ''}`}>
      <span className="inv-kpi__icon">
        <Glyph name={icon} size={18} />
      </span>
      <span className="inv-kpi__text">
        <span className="inv-kpi__label">{label}</span>
        <span className="inv-kpi__value figure">{value.toLocaleString()}</span>
        <span className="inv-kpi__sub">{sub}</span>
      </span>
    </div>
  )
}
